import { useEffect, useRef } from 'react';
import { Subject } from 'rxjs';
import { bufferCount, groupBy, map, mergeMap } from 'rxjs/operators';
import { useStorageQuota, useStorageUsageUpdateMessages } from '../../../utils/hooks/storageQuota/useStorageQuota';
import { useDuplexer } from '../../../utils/hooks/useDuplexer';
import { DuplexerEvent } from '../../../constants/DuplexerEvent';

/*
 * For every operation there are two events we subscribe for:
 *   1. Trash empty completed.
 *   2. Media quota updated.
 * The goal is to await both and combine the data.
 * If isStorageQuotaVisibilityEnabled is false, then we only await for the trash empty event.
 * */

type UsageUpdateOperation = {
  opId: string;
  type: string;
  storage: number;
};

type TrashDeleteOperation = {
  opId: string;
  type: string;
  storage: number;
  files: number;
};
type TrashEmptyOperation = { opId: string };

type Operations = TrashDeleteOperation | TrashEmptyOperation | UsageUpdateOperation;

const isUsageUpdateOperation = (x: Operations): x is UsageUpdateOperation =>
  'storage' in x && typeof x.storage === 'number' && x.type !== 'trash-delete';

const isTrashDeleteOperation = (message: Operations): message is TrashDeleteOperation =>
  'files' in message && typeof message.files === 'number' && message.type === 'trash-delete';

export const useTrashEmptyComplete = (
  onComplete: (opId: string, usageDelta?: number, filesDeleted?: number) => void,
) => {
  const { isStorageQuotaVisibilityEnabled } = useStorageQuota();
  const operationsSubject = useRef(new Subject<UsageUpdateOperation | TrashEmptyOperation>()).current;

  useDuplexer({
    [DuplexerEvent.TrashEmpty]: (message) => {
      operationsSubject.next(message.info as TrashEmptyOperation);
    },
  });
  useStorageUsageUpdateMessages((message) => {
    const { operation } = message.info.data;
    // opId is omitted if the update is external
    if (!operation.opId) {
      return;
    }

    if (operation.type === 'trash-delete') {
      operationsSubject.next(operation as UsageUpdateOperation);
    }
  }, isStorageQuotaVisibilityEnabled);

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    // operationsObservable awaits two events of the same opId and
    // emits data from both as one
    const operationsObservable = operationsSubject.pipe(
      groupBy(({ opId }) => opId),
      map((groupedObservable) =>
        groupedObservable.pipe(
          // groupBy emits GroupedObservables;
          // Map each to [Operation, Operation] if the exp is on
          // if it's off map to [Operation]
          bufferCount(isStorageQuotaVisibilityEnabled ? 2 : 1),
          map((operations) => ({
            opId: operations[0].opId, // either has opId, it's ok to just take [0]
            storage: operations.find(isUsageUpdateOperation)?.storage,
            filesDeleted: operations.find(isTrashDeleteOperation)?.files,
          })),
        ),
      ),
      // merge each observable into the resulting observable
      mergeMap((x) => x),
    );

    const subscription = operationsObservable.subscribe(({ opId, storage, filesDeleted }) => {
      onCompleteRef.current(opId, storage, filesDeleted);
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
